/**
 * Programme Application Form — appears AFTER the student has paid AND payment
 * is verified. Submitting takes the enrolment to `application_status: submitted`,
 * which then awaits admin approval before any programme content unlocks.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import Layout from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ClipboardText, CheckCircle, Warning, Clock } from "@phosphor-icons/react";

export default function ApplicationFormPage() {
  const { enrollmentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollment, setEnrollment] = useState(null);
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    full_name: user?.name || "",
    date_of_birth: "",
    phone: user?.phone || "",
    address: "",
    school_name: user?.school_name || "",
    position_title: user?.position || "",
    years_experience: "",
    highest_qualification: "",
    motivation: "",
    expectations: "",
    referee_name: "",
    referee_email: "",
    extra_notes: "",
  });

  useEffect(() => {
    Promise.all([
      api.get("/enrollments/my").then(r => (r.data || []).find(e => e.id === enrollmentId)).catch(() => null),
      api.get(`/applications/by-enrollment/${enrollmentId}`).then(r => r.data).catch(() => null),
    ]).then(([enr, app]) => {
      setEnrollment(enr || null);
      setExisting(app || null);
      setLoading(false);
    });
  }, [enrollmentId]);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.full_name || !f.motivation) return toast.error("Full name and motivation are required.");
    setSaving(true);
    try {
      await api.post("/applications", {
        enrollment_id: enrollmentId,
        ...f,
        years_experience: f.years_experience ? parseInt(f.years_experience, 10) : 0,
      });
      toast.success("Application submitted! Awaiting admin approval.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit application");
    }
    setSaving(false);
  };

  if (loading) return <Layout><div className="p-8 text-sm text-slate-400">Loading…</div></Layout>;
  if (!enrollment) return <Layout><div className="p-8 text-center"><p className="text-slate-500">Enrollment not found</p><Link to="/dashboard" className="text-blue-700 underline text-sm">Back to dashboard</Link></div></Layout>;
  const paymentOk = ["paid", "completed", "partial"].includes(enrollment.payment_status);

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="application-form-page">
        <Link to="/dashboard" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-600 font-bold">Step 3 of 4 · Application Form</p>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-950 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{enrollment.programme?.title || "Programme Application"}</h1>
          <p className="text-sm text-slate-600 mt-1">Tell us about yourself. We review every application before granting programme access.</p>
        </div>

        {!paymentOk && (
          <Card className="border border-amber-300 bg-amber-50 rounded-none mb-6"><CardContent className="p-4 flex items-center gap-3"><Warning size={18} className="text-amber-600" weight="fill" /><p className="text-sm text-amber-900">Payment must be verified by the admin before you can submit this form. Please notify your transfer first.</p></CardContent></Card>
        )}

        {existing && (
          <Card className={`border rounded-none mb-6 ${existing.status === "approved" ? "border-emerald-300 bg-emerald-50" : existing.status === "rejected" ? "border-red-300 bg-red-50" : "border-blue-200 bg-blue-50"}`}>
            <CardContent className="p-4 flex items-start gap-3">
              {existing.status === "approved" ? <CheckCircle size={20} className="text-emerald-700" weight="fill" /> : existing.status === "rejected" ? <Warning size={20} className="text-red-700" weight="fill" /> : <Clock size={20} className="text-blue-800" weight="fill" />}
              <div>
                <p className="text-sm font-bold text-slate-950 capitalize" data-testid="application-status-banner">Application {existing.status}</p>
                <p className="text-xs text-slate-700 mt-0.5">Submitted {new Date(existing.submitted_at).toLocaleString()}.{existing.review_notes ? ` Admin note: ${existing.review_notes}` : ""}</p>
                {existing.status === "submitted" && <p className="text-xs text-blue-900 mt-1">You&apos;ll get an email + dashboard notification when the admin reviews your application.</p>}
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border border-slate-200 rounded-none">
          <CardContent className="p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Full name *</Label><Input value={f.full_name} onChange={e => upd("full_name", e.target.value)} className="rounded-none" data-testid="app-full-name" /></div>
              <div><Label>Date of birth</Label><Input type="date" value={f.date_of_birth} onChange={e => upd("date_of_birth", e.target.value)} className="rounded-none" data-testid="app-dob" /></div>
              <div><Label>Phone</Label><Input value={f.phone} onChange={e => upd("phone", e.target.value)} className="rounded-none" data-testid="app-phone" /></div>
              <div><Label>Address</Label><Input value={f.address} onChange={e => upd("address", e.target.value)} className="rounded-none" data-testid="app-address" /></div>
              <div><Label>School / Employer</Label><Input value={f.school_name} onChange={e => upd("school_name", e.target.value)} className="rounded-none" data-testid="app-school" /></div>
              <div><Label>Position / title</Label><Input value={f.position_title} onChange={e => upd("position_title", e.target.value)} className="rounded-none" data-testid="app-position" /></div>
              <div><Label>Years of experience</Label><Input type="number" min="0" value={f.years_experience} onChange={e => upd("years_experience", e.target.value)} className="rounded-none" data-testid="app-years" /></div>
              <div><Label>Highest qualification</Label><Input value={f.highest_qualification} onChange={e => upd("highest_qualification", e.target.value)} className="rounded-none" data-testid="app-qualification" /></div>
            </div>
            <div><Label>Why are you applying for this programme? *</Label><Textarea rows={4} value={f.motivation} onChange={e => upd("motivation", e.target.value)} className="rounded-none" data-testid="app-motivation" placeholder="What outcomes do you want at the end?" /></div>
            <div><Label>What do you expect from the programme?</Label><Textarea rows={3} value={f.expectations} onChange={e => upd("expectations", e.target.value)} className="rounded-none" data-testid="app-expectations" /></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Referee name</Label><Input value={f.referee_name} onChange={e => upd("referee_name", e.target.value)} className="rounded-none" data-testid="app-referee-name" /></div>
              <div><Label>Referee email</Label><Input type="email" value={f.referee_email} onChange={e => upd("referee_email", e.target.value)} className="rounded-none" data-testid="app-referee-email" /></div>
            </div>
            <div><Label>Anything else we should know?</Label><Textarea rows={2} value={f.extra_notes} onChange={e => upd("extra_notes", e.target.value)} className="rounded-none" data-testid="app-extra" /></div>
            <div className="pt-4 border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <p className="text-xs text-slate-500">We review applications within 48 hours.</p>
              <Button disabled={saving || !paymentOk} onClick={submit} className="bg-blue-800 hover:bg-blue-900 text-white rounded-none gap-2" data-testid="app-submit-btn">
                <ClipboardText size={16} weight="bold" /> {saving ? "Submitting…" : existing ? "Re-submit application" : "Submit application"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
